// src/services/reviewService.js
// 리뷰 작성 시 100P 자동 적립
// markAsReviewed (reservationService) 함께 호출해야 함
// 15차: 리뷰 삭제 시 100P 차감 추가 (포인트 무한 적립 방지)
// 30차: addReview pointHistory — expiresAt (적립일+6개월), expired: false 추가
// 32차: addReview — verified: true 필드 추가 (예약 기반 인증 리뷰 배지용)
// 38차: reason 고정값 '리뷰 작성 포인트 적립' 으로 통일 (Firestore Rules 허용 목록 일치)
// 50차: addReview — spoiler 필드 저장 (스포일러 버그 수정)
//        addReview — 동일 uid + productId 중복 리뷰 방지
// 71차: addReview — storeId 저장 추가 (매장 리뷰 답글 기능이 getReviewsByStoreId로
//       조회할 수 있도록. 이전엔 storeId를 안 써서 답글 기능이 항상 빈 배열만 받았음)
// 72차: getReviewById(알림→리뷰 딥링크용), getReplyTemplate(답글 기본 템플릿) 신규
// 72차: reportReview 신규 — 리뷰 어뷰징 신고, 기존 reports 컬렉션/SuperAdmin 신고
//       관리 화면을 그대로 재사용(reviewId 필드로 구분)
// 74차: addReview — reviewerCrewName 필드 추가 (작성 시점 크루명 스냅샷,
//       reviewer 필드와 동일하게 이후 크루 탈퇴/변경과 무관하게 고정 표시)
// 78차 54차: getReviewsByProduct — slice(먼저)→filter(나중) 순서라 최근 N개 안에
//       삭제된 리뷰가 섞이면 실제보다 적게 반환되던 버그 수정 (filter 먼저, slice 나중)

import {
  doc, collection, addDoc, updateDoc, deleteField, getDoc,
  getDocs, query, where, orderBy, serverTimestamp, onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functionsAsia } from './firebase';
import { parseProductId, getStoreDisplayName } from './productService';

const REVIEW_POINT = 100;

// ── 리뷰 작성 ───────────────────────────────────────────────────────────────
// 79차: [버그 수정] escapeResult(탈출 성공/실패, 리뷰 작성 시 필수 선택)가 함수
// 시그니처/저장 payload 어디에도 없어서, WriteReviewScreen.js가 뭘 보내든 조용히
// 버려지고 있었음 — reviews 문서에 이 필드가 한 번도 저장된 적이 없어 ReviewCard.js의
// 탈출 성공/실패 배지가 항상 안 뜨고 있었을 것(전수 정독으로 발견). 파라미터/저장
// 필드 추가.
export const addReview = async ({ uid, reviewer, reviewerCrewName = null, productId, productName, reservationId, rating, comment, imageUris = [], subRatings = null, spoiler = false, escapeResult = null }) => {
  // 50차: 동일 uid + productId 중복 리뷰 방지
  // uid 단일 인덱스로 조회 후 클라이언트 필터 (복합 인덱스 불필요)
  try {
    const existingSnap = await getDocs(
      query(collection(db, 'reviews'), where('uid', '==', uid))
    );
    const alreadyReviewed = existingSnap.docs.some(
      d => !d.data().deleted && d.data().productId === productId
    );
    if (alreadyReviewed) {
      throw new Error('이미 리뷰를 작성한 테마입니다.');
    }
  } catch (e) {
    if (e.message === '이미 리뷰를 작성한 테마입니다.') throw e;
    // 중복 체크 조회 실패 시 조용히 통과 (서비스 중단 방지)
  }

  const storeId = parseProductId(productId)?.storeId || null;

  const docRef = await addDoc(collection(db, 'reviews'), {
    uid,
    reviewer,
    reviewerCrewName,
    productId,
    productName,
    // 71차: 매장 리뷰 답글 기능(getReviewsByStoreId)이 조회할 수 있도록 storeId 저장
    storeId,
    reservationId,
    rating:     Number(rating),
    comment:    comment || '',
    content:    comment || '',
    imageUris,
    imageUrls:  imageUris,
    // 항목별 점수 (선택)
    subRatings: subRatings || null,
    // 32차: 예약 ID가 있으면 인증 리뷰로 표시
    verified:   !!reservationId,
    // 50차: 스포일러 포함 여부 저장
    spoiler:    !!spoiler,
    // 79차: 탈출 성공/실패 — ReviewCard.js가 이 필드로 배지를 그림
    escapeResult: escapeResult === null ? null : !!escapeResult,
    createdAt:  new Date().toISOString(),
  });

  // reservation.reviewed = true
  try {
    await updateDoc(doc(db, 'reservations', reservationId), { reviewed: true });
  } catch { /* 무음 처리 */ }

  // 78차: 포인트 100P 적립 — grantPointsCF를 클라이언트가 호출하던 것을 삭제하고
  // functions/index.js의 onNewReview 트리거(Admin SDK) 안으로 이관(전수조사로 발견한
  // 위조 호출 취약점 수정 — 리뷰가 실제로 방금 이 함수로 생성됐을 때만 서버가 자동으로
  // 지급, 클라이언트가 별도로 요청할 필요도 방법도 없어짐).

  // 75차: 테마 rating/reviewCount 갱신 — 여기서 클라이언트가 stores 문서의 branches를
  // 통째로 직접 updateDoc하고 있었음. firestore.rules가 "로그인한 아무나 branches 필드만
  // 바꾸면 통과"라는 넓은 예외를 두고 있었던 이유가 바로 이 코드 때문이었는데, 뒤집어
  // 보면 악의적인 사용자가 자기 매장이 아닌 다른 매장의 테마 데이터를 통째로 덮어쓸 수
  // 있는 구멍이기도 했음. functions/index.js의 onNewReview 트리거(Admin SDK)로 이관.
  return docRef.id;
};

// ── 특정 테마 리뷰 조회 ──────────────────────────────────────────────────────
export const getReviewsByProduct = async (productId, limitCount = 20) => {
  const q = query(
    collection(db, 'reviews'),
    where('productId', '==', productId),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => !r.deleted)
    .slice(0, limitCount);
};

// ── 내 리뷰 목록 ────────────────────────────────────────────────────────────
export const getMyReviews = async (uid) => {
  const q = query(
    collection(db, 'reviews'),
    where('uid', '==', uid),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => !r.deleted);
};

// ── 리뷰 삭제 (soft delete + 포인트 차감) ────────────────────────────────────
// 15차: 삭제 시 적립했던 100P 회수 → 재작성으로 포인트 무한 적립 방지
export const deleteReview = async (reviewId) => {
  try {
    const snap = await getDoc(doc(db, 'reviews', reviewId));
    if (!snap.exists()) return;
    const review = snap.data();

    if (review.deleted) return;

    await updateDoc(doc(db, 'reviews', reviewId), { deleted: true });

    // 75차: 여기도 addReview와 동일한 문제 — users.points를 클라이언트에서 직접
    // updateDoc하고 있어 firestore.rules에 막혀 항상 조용히 실패하고 있었음. 크루/모집
    // 상단노출이 쓰는 usePointsFIFO_CF(reason 화이트리스트 없이 본인 포인트만 차감)로 교체.
    // 포인트가 이미 다른 곳에 쓰여 부족하면(INSUFFICIENT_POINTS) 리뷰 삭제 자체는 막지 않고
    // 무음 처리(기존 의도 유지).
    // 79차: [버그 수정] 지금까지 review.uid만 있으면 무조건 100P를 차감했음 — 같은 예약에
    // 리뷰를 다시 쓰면(onNewReview가 reviewPointsGranted 플래그로 재지급을 정확히 막음)
    // 그 두 번째 리뷰는 애초에 포인트를 받은 적이 없는데, 지우면 여기서 또 100P를 깎아서
    // 정당하게 갖고 있던 포인트가 write→delete를 반복할 때마다 근거 없이 계속 줄어들었음
    // (fork로 발견). onNewReview가 남기는 review.pointsGranted가 false일 때만(=이 리뷰는
    // 지급 대상이 아니었음) 차감을 건너뜀 — 이 필드가 아직 없는 과거 리뷰(undefined)는
    // 지금까지의 동작(무조건 차감)과 동일하게 처리.
    if (review.uid && review.pointsGranted !== false) {
      try {
        const cf = httpsCallable(functionsAsia, 'usePointsFIFO_CF');
        await cf({ uid: review.uid, amount: REVIEW_POINT, reason: `${review.productName || ''} 리뷰 삭제 차감` });
      } catch { /* 무음 처리 */ }
    }

    if (review.reservationId) {
      try {
        await updateDoc(doc(db, 'reservations', review.reservationId), { reviewed: false });
      } catch { /* 무음 처리 */ }
    }
  } catch (e) {
    throw e;
  }
};

// ─── 72차 신규: 단건 리뷰 조회 (알림 탭 → 리뷰 답글 딥링크용) ──
export const getReviewById = async (reviewId) => {
  if (!reviewId) return null;
  const snap = await getDoc(doc(db, 'reviews', reviewId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// 72차: 리뷰 답변 기본 템플릿 — "안녕하세요, {매장명_지점명}입니다..." 문구를
// 리뷰가 달린 지점 기준으로 생성 (다지점 매장에서도 항상 맞는 지점명이 나오도록
// productId를 파싱해 branchIdx를 구함)
export const getReplyTemplate = (store, review) => {
  const branchIdx  = parseProductId(review?.productId)?.branchIdx;
  const branchName = branchIdx != null ? store?.branches?.[branchIdx]?.branchName : null;
  const signature   = branchName ? `${getStoreDisplayName(store)}_${branchName}` : getStoreDisplayName(store);
  return `안녕하세요, ${signature}입니다. 소중한 리뷰 남겨주셔서 감사합니다 :)`;
};

// ─── 매장 리뷰 목록 조회 (storeId 기준) ─────────────────────
// StoreAdminScreen에서 답변 관리용
export const getReviewsByStoreId = async (storeId) => {
  if (!storeId) return [];
  const q = query(
    collection(db, 'reviews'),
    where('storeId', '==', storeId),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => !r.deleted);
};


// ─── 매장 리뷰 답변 등록/수정 ────────────────────────────────
export const replyToReview = async (reviewId, adminUid, replyText) => {
  if (!reviewId || !adminUid || !replyText?.trim()) {
    throw new Error('필수 파라미터 누락');
  }
  await updateDoc(doc(db, 'reviews', reviewId), {
    reply: {
      text:      replyText.trim(),
      adminUid,
      repliedAt: new Date().toISOString(),
    },
  });
};

// ─── 매장 리뷰 답변 삭제 ─────────────────────────────────────
export const deleteReply = async (reviewId) => {
  await updateDoc(doc(db, 'reviews', reviewId), {
    reply: deleteField(),
  });
};

// ─── 72차 신규: 리뷰 어뷰징 신고 ─────────────────────────────
// 기존 reports 컬렉션(모집글 신고 전용, recruitId 기준)을 그대로 재사용하되
// reviewId 필드로 리뷰 신고를 구분한다. targetUid를 리뷰 작성자로 채우면
// SuperAdminUserReportScreen의 기존 신고 관리 UI(누적 신고 횟수, 처리/기각,
// 대상자 알림 발송)를 그대로 재사용할 수 있다 — 화면 쪽은 손댈 필요 없음.
export const reportReview = async ({ reviewId, reporterUid, reporterName, review, reason }) => {
  if (!reviewId || !reporterUid || !reason) throw new Error('필수 파라미터 누락');

  const dupSnap = await getDocs(query(
    collection(db, 'reports'),
    where('reporterUid', '==', reporterUid),
    where('reviewId', '==', reviewId),
  ));
  if (!dupSnap.empty) throw new Error('ALREADY_REPORTED');

  await addDoc(collection(db, 'reports'), {
    reviewId,
    reporterUid,
    reporterName,
    targetUid: review?.uid || null,
    targetName: review?.reviewer || '익명',
    reason,
    title: review?.productName ? `리뷰 신고 — ${review.productName}` : '리뷰 신고',
    createdAt: serverTimestamp(),
    status: 'pending',
  });
};

// 75차: "작성 가능한 리뷰" 개수 — MyPageScreen.js의 "내 리뷰" 뱃지가 review_reminder
// 알림 개수(안 읽은 리마인더 알림 수)를 쓰고 있었는데, 이건 MyReviewsScreen.js의 "작성
// 가능" 탭이 실제로 계산하는 기준(지난 예약 중 취소 안 되고 아직 리뷰 안 쓴 것)과
// 전혀 다른 값이라 서로 어긋났음. 두 화면이 같은 계산을 쓰도록 여기로 추출.
// 78차 67차: r.date < todayStr(달력일 기준)만 보고 있어서, 매장이 결과(success)를 이미
// 입력한 "오늘" 예약은 다음날까지 어디에도 "작성 가능"으로 안 잡히던 버그 — 매장이 결과를
// 입력했으면(success!=null) 그 자체로 종료된 것으로 보고, 그게 아니면 예약 시작 시각이
// 지났는지(날짜만이 아니라 시간까지)로 판단하도록 확장.
const filterWriteable = (docs) => {
  const now = new Date();
  return docs.filter(r => {
    if (!r.date || r.cancelled === true || r.reviewed || r.paymentStatus === 'pending') return false;
    if (r.success === true || r.success === false) return true;
    return new Date(`${r.date}T${r.time || '00:00'}`) <= now;
  });
};

export const getWriteableReservations = async (uid) => {
  const snap = await getDocs(query(collection(db, 'reservations'), where('uid', '==', uid)));
  return filterWriteable(snap.docs.map(d => ({ id: d.id, ...d.data() })));
};

// 78차 69차: 하단 탭 배지(GeneralTabNavigator)가 이 값을 마운트 시 한 번만 fetch해서,
// 리뷰를 작성해 reservations.reviewed가 true로 바뀌어도 화면을 나갔다 들어오기 전까진
// 배지가 안 사라지던 버그 — 실시간 구독으로 교체(다른 두 탭 배지 리스너와 동일 패턴).
export const listenWriteableReservationsCount = (uid, callback) => {
  return onSnapshot(
    query(collection(db, 'reservations'), where('uid', '==', uid)),
    snap => callback(filterWriteable(snap.docs.map(d => ({ id: d.id, ...d.data() }))).length),
    () => callback(0),
  );
};